import React, { useState, useEffect } from 'react';
import { database, storage } from '../firebase';
import { ref as dbRef, set, onValue } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  FaQrcode,
  FaUpload,
  FaCheckCircle,
  FaExclamationTriangle,
  FaSpinner,
  FaImage,
  FaEdit,
  FaSave,
} from 'react-icons/fa';
import { motion } from 'framer-motion';

const AdminPaymentQR = () => {
  const [googlePayQR, setGooglePayQR] = useState(null);
  const [phonepeQR, setPhonepeQR] = useState(null);
  const [googlePayPreview, setGooglePayPreview] = useState('');
  const [phonepePreview, setPhonepePreview] = useState('');
  const [googlePayFile, setGooglePayFile] = useState(null);
  const [phonepeFile, setPhonepeFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState({ googlePay: false, phonePe: false });
  const [success, setSuccess] = useState({ googlePay: false, phonePe: false });
  const [error, setError] = useState({ googlePay: '', phonePe: '' });

  // Fetch existing QR codes from Firebase
  useEffect(() => {
    const qrRef = dbRef(database, 'Dr/paymentQR');
    
    const unsubscribe = onValue(qrRef, (snapshot) => {
      try {
        if (snapshot.exists()) {
          const data = snapshot.val();
          if (data.googlePay) {
            setGooglePayQR(data.googlePay);
            setGooglePayPreview(data.googlePay);
          }
          if (data.phonePe) {
            setPhonepeQR(data.phonePe);
            setPhonepePreview(data.phonePe);
          }
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching QR codes:', err);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  // Handle Google Pay QR upload
  const handleGooglePayUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        setGooglePayFile(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          setGooglePayPreview(reader.result);
        };
        reader.readAsDataURL(file);
        setError(prev => ({ ...prev, googlePay: '' }));
        setSuccess(prev => ({ ...prev, googlePay: false }));
      } else {
        setError(prev => ({ ...prev, googlePay: 'Please upload a valid image file' }));
      }
    }
  };

  // Handle PhonePe QR upload
  const handlePhonepeUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        setPhonepeFile(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          setPhonepePreview(reader.result);
        };
        reader.readAsDataURL(file);
        setError(prev => ({ ...prev, phonePe: '' }));
        setSuccess(prev => ({ ...prev, phonePe: false }));
      } else {
        setError(prev => ({ ...prev, phonePe: 'Please upload a valid image file' }));
      }
    }
  };

  // Save Google Pay QR to Firebase
  const handleSaveGooglePay = async () => {
    if (!googlePayFile) {
      setError(prev => ({ ...prev, googlePay: 'Please select an image first' }));
      return;
    }

    setUploading(prev => ({ ...prev, googlePay: true }));
    setError(prev => ({ ...prev, googlePay: '' }));

    try {
      // Upload to Firebase Storage
      const timestamp = Date.now();
      const fileName = `googlepay_qr_${timestamp}.${googlePayFile.name.split('.').pop()}`;
      const imageRef = storageRef(storage, `Dr/paymentQR/${fileName}`);
      
      await uploadBytes(imageRef, googlePayFile);
      const downloadURL = await getDownloadURL(imageRef);

      // Save URL to Firebase Database
      const qrRef = dbRef(database, 'Dr/paymentQR/googlePay');
      await set(qrRef, downloadURL);

      setGooglePayQR(downloadURL);
      setSuccess(prev => ({ ...prev, googlePay: true }));
      setGooglePayFile(null);
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setSuccess(prev => ({ ...prev, googlePay: false }));
      }, 3000);
    } catch (err) {
      console.error('Error uploading Google Pay QR:', err);
      setError(prev => ({ ...prev, googlePay: 'Failed to upload QR code. Please try again.' }));
    } finally {
      setUploading(prev => ({ ...prev, googlePay: false }));
    }
  };

  // Save PhonePe QR to Firebase
  const handleSavePhonePe = async () => {
    if (!phonepeFile) {
      setError(prev => ({ ...prev, phonePe: 'Please select an image first' }));
      return;
    }

    setUploading(prev => ({ ...prev, phonePe: true }));
    setError(prev => ({ ...prev, phonePe: '' }));

    try {
      // Upload to Firebase Storage
      const timestamp = Date.now();
      const fileName = `phonepe_qr_${timestamp}.${phonepeFile.name.split('.').pop()}`;
      const imageRef = storageRef(storage, `Dr/paymentQR/${fileName}`);
      
      await uploadBytes(imageRef, phonepeFile);
      const downloadURL = await getDownloadURL(imageRef);

      // Save URL to Firebase Database
      const qrRef = dbRef(database, 'Dr/paymentQR/phonePe');
      await set(qrRef, downloadURL);

      setPhonepeQR(downloadURL);
      setSuccess(prev => ({ ...prev, phonePe: true }));
      setPhonepeFile(null);
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setSuccess(prev => ({ ...prev, phonePe: false }));
      }, 3000);
    } catch (err) {
      console.error('Error uploading PhonePe QR:', err);
      setError(prev => ({ ...prev, phonePe: 'Failed to upload QR code. Please try again.' }));
    } finally {
      setUploading(prev => ({ ...prev, phonePe: false }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="text-5xl text-teal-500 mx-auto mb-4 animate-spin" />
          <p className="text-gray-600">Loading payment QR codes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-bold text-gray-800 mb-2 flex items-center">
            <FaQrcode className="mr-3 text-teal-600" />
            Payment QR Code Management
          </h1>
          <p className="text-gray-600">
            Upload and manage QR codes for Google Pay and PhonePe payments
          </p>
        </motion.div>

        {/* QR Code Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Google Pay QR */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200"
          >
            <div className="flex items-center mb-6">
              <div className="bg-gradient-to-r from-blue-500 to-cyan-500 p-3 rounded-xl mr-3">
                <FaQrcode className="text-2xl text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-800">Google Pay</h2>
                <p className="text-sm text-gray-600">Upload Google Pay QR Code</p>
              </div>
            </div>

            {/* Current QR Display */}
            {googlePayPreview && (
              <div className="mb-6 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-4 border border-blue-200">
                <p className="text-sm font-bold text-gray-700 mb-3 flex items-center">
                  <FaImage className="mr-2 text-blue-600" />
                  {googlePayQR ? 'Current QR Code' : 'Preview'}
                </p>
                <div className="bg-white p-4 rounded-lg flex justify-center">
                  <img
                    src={googlePayPreview}
                    alt="Google Pay QR"
                    className="max-w-full max-h-64 rounded-lg"
                  />
                </div>
              </div>
            )}

            {/* Upload Section */}
            <div className="mb-4">
              <label className="block text-sm font-bold text-gray-700 mb-2">
                <FaUpload className="inline mr-2 text-teal-500" />
                {googlePayQR ? 'Replace QR Code' : 'Upload QR Code'}
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleGooglePayUpload}
                className="hidden"
                id="googlePayUpload"
              />
              <label
                htmlFor="googlePayUpload"
                className="block w-full p-4 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-blue-500 transition-all text-center"
              >
                <FaUpload className="text-3xl text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600">
                  Click to {googlePayFile ? 'change' : 'upload'} image
                </p>
              </label>
            </div>

            {/* Success Message */}
            {success.googlePay && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-xl text-green-700 text-sm flex items-center">
                <FaCheckCircle className="mr-2" />
                QR code uploaded successfully!
              </div>
            )}

            {/* Error Message */}
            {error.googlePay && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-center">
                <FaExclamationTriangle className="mr-2" />
                {error.googlePay}
              </div>
            )}

            {/* Save Button */}
            <button
              onClick={handleSaveGooglePay}
              disabled={!googlePayFile || uploading.googlePay}
              className={`w-full py-3 rounded-xl font-bold text-white flex items-center justify-center space-x-2 transition-all ${
                !googlePayFile || uploading.googlePay
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-500 to-cyan-500 hover:shadow-lg'
              }`}
            >
              {uploading.googlePay ? (
                <>
                  <FaSpinner className="animate-spin" />
                  <span>Uploading...</span>
                </>
              ) : (
                <>
                  <FaSave />
                  <span>Save Google Pay QR</span>
                </>
              )}
            </button>
          </motion.div>

          {/* PhonePe QR */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200"
          >
            <div className="flex items-center mb-6">
              <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-3 rounded-xl mr-3">
                <FaQrcode className="text-2xl text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-800">PhonePe</h2>
                <p className="text-sm text-gray-600">Upload PhonePe QR Code</p>
              </div>
            </div>

            {/* Current QR Display */}
            {phonepePreview && (
              <div className="mb-6 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 border border-purple-200">
                <p className="text-sm font-bold text-gray-700 mb-3 flex items-center">
                  <FaImage className="mr-2 text-purple-600" />
                  {phonepeQR ? 'Current QR Code' : 'Preview'}
                </p>
                <div className="bg-white p-4 rounded-lg flex justify-center">
                  <img
                    src={phonepePreview}
                    alt="PhonePe QR"
                    className="max-w-full max-h-64 rounded-lg"
                  />
                </div>
              </div>
            )}

            {/* Upload Section */}
            <div className="mb-4">
              <label className="block text-sm font-bold text-gray-700 mb-2">
                <FaUpload className="inline mr-2 text-teal-500" />
                {phonepeQR ? 'Replace QR Code' : 'Upload QR Code'}
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handlePhonepeUpload}
                className="hidden"
                id="phonepeUpload"
              />
              <label
                htmlFor="phonepeUpload"
                className="block w-full p-4 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-purple-500 transition-all text-center"
              >
                <FaUpload className="text-3xl text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600">
                  Click to {phonepeFile ? 'change' : 'upload'} image
                </p>
              </label>
            </div>

            {/* Success Message */}
            {success.phonePe && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-xl text-green-700 text-sm flex items-center">
                <FaCheckCircle className="mr-2" />
                QR code uploaded successfully!
              </div>
            )}

            {/* Error Message */}
            {error.phonePe && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-center">
                <FaExclamationTriangle className="mr-2" />
                {error.phonePe}
              </div>
            )}

            {/* Save Button */}
            <button
              onClick={handleSavePhonePe}
              disabled={!phonepeFile || uploading.phonePe}
              className={`w-full py-3 rounded-xl font-bold text-white flex items-center justify-center space-x-2 transition-all ${
                !phonepeFile || uploading.phonePe
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'bg-gradient-to-r from-purple-500 to-pink-500 hover:shadow-lg'
              }`}
            >
              {uploading.phonePe ? (
                <>
                  <FaSpinner className="animate-spin" />
                  <span>Uploading...</span>
                </>
              ) : (
                <>
                  <FaSave />
                  <span>Save PhonePe QR</span>
                </>
              )}
            </button>
          </motion.div>
        </div>

        {/* Instructions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-8 bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6 border border-blue-200"
        >
          <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center">
            <FaExclamationTriangle className="mr-2 text-blue-600" />
            Instructions
          </h3>
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>Upload clear, high-quality QR code images for better scanning</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>Supported formats: JPG, PNG, GIF</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>These QR codes will be displayed to customers during checkout</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>Make sure the QR codes are linked to your business payment accounts</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>You can update the QR codes anytime by uploading new images</span>
            </li>
          </ul>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminPaymentQR;
